// src/pages/components/CheckoutForm.jsx

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Typography, CircularProgress, Box } from "@mui/material"; // Import additional components for feedback
import { useSelector } from "react-redux";
import { authenticatedFetch } from "../../network/api";

// Initialize Stripe.js outside of component render to avoid recreating Stripe object on every render
const CheckoutForm = () => {
  const location = useLocation(); // Hook to access the current location

  // Extract query parameters using URLSearchParams
  const queryParams = new URLSearchParams(location.search);
  const lawyerId = queryParams.get('lawyerId');
  const lawyerName = queryParams.get('lawyerName');
  const date = queryParams.get('date');
  const startTime = queryParams.get('startTime');
  const endTime = queryParams.get('endTime');
  const priceId = queryParams.get('priceId')
  const productId = queryParams.get('productId')
  const accountId = queryParams.get('accountId')
  const price = queryParams.get('price')
  const practiceAreas = queryParams.get("practiceAreas")
  const appointmentName = queryParams.get('appointmentName')
  const requestId = queryParams.get('requestId')
  const lawyerPhoto = queryParams.get('lawyerPhoto')

  // Define required parameters for creating a Checkout Session
  const requiredParams = [lawyerId, priceId, productId, accountId, date, startTime, endTime];
  const hasAllRequiredParams = requiredParams.every(param => param !== null && param !== undefined);

  // State for handling errors and loading status
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const userId = useSelector((state) => state.clientData.clientData?.user_id);
  const customerId = useSelector((state) => state.clientData.clientData?.stripe_customer_id);

  // Function to check appointment price and then create checkout session
  const fetchCheckoutSession = async () => {
    // Check if all required parameters are present
    if (!hasAllRequiredParams) {
      const missingParams = requiredParams.filter(param => param === null || param === undefined);
      const error = `Missing required parameter(s): ${missingParams.join(', ')}`;
      setErrorMessage(error);
      console.error(error);
      return;
    }

    setIsLoading(true); // Start loading indicator

    try {
      // Step 1: Check appointment price based on client-lawyer history
      const priceCheckResponse = await authenticatedFetch(`/api/client/webapp/checkAppointmentPrice?client_id=${userId}&lawyer_id=${lawyerId}&product_id=${productId}`);
      
      if (!priceCheckResponse.ok) {
        throw new Error(`Failed to check appointment price: ${priceCheckResponse.statusText}`);
      }
      
      const priceData = await priceCheckResponse.json();
      console.log('Appointment price data:', priceData);
      
      // Use the price from the API response instead of the URL parameter
      const finalPrice = priceData.price;

      // Step 2: Create checkout session with the determined price
      const checkoutResponse = await authenticatedFetch("/api/client/webapp/createCheckoutSession", {
        method: "POST",
        body: JSON.stringify({
          userId,
          customerId,
          stripeProductId: productId, 
          stripePriceId: priceId,
          connectedAccountId: accountId,
          date,
          startTime,
          endTime,
          lawyerName,
          price: finalPrice, // Use the price from the API response
          practiceAreas,
          appointmentName,
          requestId,
          lawyerPhoto,
        }),
      });

      if (!checkoutResponse.ok) {
        throw new Error(`Network response was not ok: ${checkoutResponse.statusText}`);
      }

      const data = await checkoutResponse.json();
      console.log(data);
      
      if (data && data.clientURL) {
        // Redirect to the URL received in the response
        window.location.href = data.clientURL;
      } else {
        throw new Error('URL not found in response');
      }
    } catch (err) {
      setErrorMessage(err.message);
      console.error('Error in checkout process:', err);
    } finally {
      setIsLoading(false); // End loading indicator
    }
  };

  // Trigger the Checkout session on component mount
  useEffect(() => {
    fetchCheckoutSession();
  }, []); // Empty dependency array ensures it runs only once after component mount

  // If there is an error with missing params, display it to the user
  if (!hasAllRequiredParams) {
    return (
      <Box id="checkout" display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
        <Typography variant="h6" color="error" gutterBottom>
          {errorMessage || 'Missing required appointment details.'}
        </Typography>
      </Box>
    );
  }

  return (
    <div id="checkout">
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <CircularProgress />
        </Box>
      )}
      {errorMessage && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <Typography variant="body1" color="error">
            {errorMessage}
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default CheckoutForm;
